import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { GENERATE_OTP, VERIFY_OTP } from '@/Graphql/mutation';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import { handleError } from '@/lib/utils';
import LoginCard from '@/pages/authentication/components/LoginCard';
import { getRoutes } from '@/routes/routePaths';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { z } from 'zod';

interface LoginProps {
  otp: string;
}

const LoginSchema = z.object({
  otp: z.string().min(6, { message: 'Enter the 6-digit otp' }),
});

const VerifyOtpComponent = () => {
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber;
  const countryCode = location.state?.countryCode;
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [isActive, setIsActive] = useState<boolean>(true);

  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm<LoginProps>({
    resolver: zodResolver(LoginSchema),
  });

  const { getCurrentUser } = useGetCurrentUser();

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (isActive && timeLeft > 0) {
      timerId = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    }

    return () => clearTimeout(timerId);
  }, [timeLeft, isActive]);

  const resetTimer = () => {
    setTimeLeft(60);
    setIsActive(true);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}`;
  };
  const [verify, { loading }] = useMutation(VERIFY_OTP, {
    onCompleted: (res) => {
      localStorage.setItem('accessToken', res?.verifyOtp?.accessToken);
      localStorage.setItem('refreshToken', res?.verifyOtp?.refreshToken);
      getCurrentUser();
    },
    onError: handleError,
  });

  const [generateOtp] = useMutation(GENERATE_OTP, {
    onCompleted: () => {
      resetTimer();
    },
  });

  const handleResendOtp = () => {
    generateOtp({ variables: { countryCode, phoneNumber } });
  };

  const onSubmit = handleSubmit((data) => {
    verify({ variables: { countryCode, phoneNumber, otp: data.otp } });
  });

  return (
    <form onSubmit={onSubmit}>
      <div className='flex flex-col w-full gap-10'>
        <div className='flex flex-col items-center gap-2'>
          <InputOTP
            autoFocus
            // value={watch('otp')}
            {...register('otp')}
            onChange={(val) => setValue('otp', val)}
            maxLength={6}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={1} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          {errors.otp?.message && (
            <p
              aria-label='error'
              aria-errormessage={errors.otp?.message}
              className='text-red text-xs w-full text-left'
            >
              {errors?.otp?.message}
            </p>
          )}
          {timeLeft > 0 && (
            <h5 className='font-normal'>
              Time remaining <b>{formatTime(timeLeft)}</b>
            </h5>
          )}
        </div>
        <div className='flex flex-col items-center gap-3'>
          {timeLeft === 0 && (
            <h5 className='font-normal'>
              Didn’t get a code?{' '}
              <Button
                id='resend-btn'
                onClick={handleResendOtp}
                variant='ghost'
                className='p-0 w-min h-min'
              >
                <b>Resend Code</b>
              </Button>
            </h5>
          )}
          <Button type='submit' id='button' loading={loading} className='w-full font-bold h-12'>
            Verify
          </Button>
          <h5 className='font-normal mt-3'>
            Entered the wrong number?{' '}
            <Link to={getRoutes.Login} replace={true} className='p-0 w-min h-min'>
              <b>Edit Phone Number.</b>
            </Link>
          </h5>
        </div>
      </div>
    </form>
  );
};

const VerifyOtp = () => {
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber;
  const countryCode = location.state?.countryCode;
  if (!phoneNumber || !countryCode) {
    return <Navigate to={getRoutes.Login} />;
  }
  return (
    <LoginCard
      description={
        <>
          A 6-digit code was sent to{' '}
          <b className='text-white'>{formatPhoneNumberIntl(countryCode + ' ' + phoneNumber)}</b>{' '}
          and your registered email address.
        </>
      }
      title='ENTER THE CODE YOU RECEIVED'
      content={<VerifyOtpComponent />}
    />
  );
};
export default VerifyOtp;
